import axiosInstance from "api/axiosInstance";

import type {
  LessonProductListItem,
  LessonProductDetail,
  LessonProductCreateRequest,
  LessonOptionGroup,
  LessonOptionGroupCreateRequest,
  LessonOptionValue,
  LessonOptionValueCreateRequest,
  // PaginatedLessonProductResponse,
} from "api/types/lesson-product.types";

const BASE = "/server/buccl_lessons/api/v1/lesson-products/";

const toFormData = (payload: LessonProductCreateRequest): FormData => {
  const fd = new FormData();
  const set = (key: string, val: unknown) => {
    if (val === undefined || val === null) return;
    fd.append(key, String(val));
  };
  set("sport", payload.sport);
  set("name", payload.name);
  if (payload.brand !== undefined) set("brand", payload.brand);
  if (payload.detailed_content !== undefined) set("detailed_content", payload.detailed_content);
  if (payload.sessions_count !== undefined) set("sessions_count", payload.sessions_count);
  set("price", payload.price);
  if (payload.discount_price != null) set("discount_price", payload.discount_price);
  if (payload.validity_period !== undefined) set("validity_period", payload.validity_period);
  if (payload.ticket_type !== undefined) set("ticket_type", payload.ticket_type);
  if (payload.theory_required_before_day != null) set("theory_required_before_day", payload.theory_required_before_day);
  if (payload.short_description !== undefined) set("short_description", payload.short_description);
  if (payload.is_active !== undefined) set("is_active", payload.is_active);
  if (payload.is_featured !== undefined) set("is_featured", payload.is_featured);
  if (payload.is_retake_product !== undefined) set("is_retake_product", payload.is_retake_product);
  payload.images?.forEach((img) => fd.append("images", img));
  payload.image_titles?.forEach((t) => fd.append("image_titles", t ?? ""));
  payload.image_alt_texts?.forEach((t) => fd.append("image_alt_texts", t ?? ""));
  payload.image_thumbnails?.forEach((t) => fd.append("image_thumbnails", String(t)));
  payload.image_orders?.forEach((o) => fd.append("image_orders", String(o)));
  return fd;
};

export const lessonProductsService = {
  list: (params?: { page?: number; is_active?: boolean; search?: string }) => axiosInstance.get<LessonProductListItem[]>(BASE, { params }),

  get: (id: number) => axiosInstance.get<LessonProductDetail>(`${BASE}${id}/`),

  create: (payload: LessonProductCreateRequest) => {
    if (payload.images && payload.images.length > 0) {
      return axiosInstance.post<LessonProductDetail>(BASE, toFormData(payload), {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return axiosInstance.post<LessonProductDetail>(BASE, payload);
  },

  update: (id: number, payload: Partial<LessonProductCreateRequest>) => {
    if (payload.images && payload.images.length > 0) {
      return axiosInstance.patch<LessonProductDetail>(`${BASE}${id}/`, toFormData(payload as LessonProductCreateRequest), { headers: { "Content-Type": "multipart/form-data" } });
    }
    return axiosInstance.patch<LessonProductDetail>(`${BASE}${id}/`, payload);
  },

  remove: (id: number) => axiosInstance.delete(`${BASE}${id}/`),

  // 상세 내용 에디터용 이미지 업로드 (URL 반환)
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return axiosInstance.post<{ url: string; filename: string; size: number; message: string }>("/server/buccl_main/api/v1/upload-image/lesson-product/", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },

  // ── Option Groups ───────────────────────────────────────────────────────────
  getOptionGroups: (productId: number) => axiosInstance.get<LessonOptionGroup[]>(`${BASE}${productId}/options/`),

  createOptionGroup: (productId: number, data: LessonOptionGroupCreateRequest) => axiosInstance.post<LessonOptionGroup>(`${BASE}${productId}/options/`, data),

  updateOptionGroup: (productId: number, groupId: number, data: Partial<LessonOptionGroupCreateRequest>) => axiosInstance.patch<LessonOptionGroup>(`${BASE}${productId}/options/${groupId}/`, data),

  deleteOptionGroup: (productId: number, groupId: number) => axiosInstance.delete(`${BASE}${productId}/options/${groupId}/`),

  // ── Option Values ───────────────────────────────────────────────────────────
  createOptionValue: (productId: number, groupId: number, data: LessonOptionValueCreateRequest) => axiosInstance.post<LessonOptionValue>(`${BASE}${productId}/options/${groupId}/values/`, data),

  updateOptionValue: (productId: number, groupId: number, valueId: number, data: Partial<LessonOptionValueCreateRequest>) => axiosInstance.patch<LessonOptionValue>(`${BASE}${productId}/options/${groupId}/values/${valueId}/`, data),

  deleteOptionValue: (productId: number, groupId: number, valueId: number) => axiosInstance.delete(`${BASE}${productId}/options/${groupId}/values/${valueId}/`),
};
